/**
 * CandidateNotificationSettings — /candidate/settings/notifications
 *
 * V1.7.5 (Feb 11, 2026) — Progressive access to the push channel.
 * Candidates control which categories fire a Web Push so the daily
 * 3-notification budget goes toward the things they actually want.
 *
 * Wired to existing V1.7.0 backend:
 *   GET   /api/me/notification-prefs   — read current
 *   PATCH /api/me/notification-prefs   — write partial
 *
 * Prefs shape (server-side default = True for everything except marketing):
 *   chat_reply | interview | offer | stage_change | job_match | team_chat | marketing
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Bell,
    ChatCircleDots,
    Calendar,
    Trophy,
    ListChecks,
    Briefcase,
    Users,
    Megaphone,
    CheckCircle,
} from "@phosphor-icons/react";

const CATEGORIES = [
    {
        key: "chat_reply",
        label: "Chat replies",
        desc: "When a recruiter replies to your DaddyTalk chat.",
        icon: ChatCircleDots,
        priority: "highest",
    },
    {
        key: "interview",
        label: "Interview scheduled or updated",
        desc: "Slot booked, rescheduled, or cancelled — never miss one.",
        icon: Calendar,
        priority: "highest",
    },
    {
        key: "offer",
        label: "Job offers",
        desc: "You got an offer — we ping you instantly.",
        icon: Trophy,
        priority: "highest",
    },
    {
        key: "stage_change",
        label: "Pipeline movement",
        desc: "Moved to Screening, Shortlisted, or a later stage.",
        icon: ListChecks,
        priority: "high",
    },
    {
        key: "job_match",
        label: "New job matches",
        desc: "A role that fits your CV just opened up.",
        icon: Briefcase,
        priority: "medium",
    },
    {
        key: "team_chat",
        label: "Team messages",
        desc: "Only relevant if you're a staff user with TeamTalk access.",
        icon: Users,
        priority: "medium",
        staffOnly: true,
    },
    {
        key: "marketing",
        label: "Product tips & offers",
        desc: "Occasional feature drops, promotions, referral bonuses.",
        icon: Megaphone,
        priority: "low",
    },
];

export default function CandidateNotificationSettings() {
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/me/notification-prefs");
            setPrefs(res?.data || {});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load preferences");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function toggle(key) {
        if (savingKey || !prefs) return;
        const nextVal = !prefs[key];
        // Optimistic update
        setPrefs((p) => ({ ...p, [key]: nextVal }));
        setSavingKey(key);
        try {
            const res = await api.patch("/me/notification-prefs", { [key]: nextVal });
            setPrefs(res?.data || prefs);
            toast.success(`${nextVal ? "Enabled" : "Muted"} · ${CATEGORIES.find((c) => c.key === key)?.label}`);
        } catch (err) {
            // Roll back
            setPrefs((p) => ({ ...p, [key]: !nextVal }));
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally { setSavingKey(null); }
    }

    return (
        <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6" data-testid="notif-prefs-root">
            <header className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-cobalt/10 flex items-center justify-center">
                        <Bell size={22} weight="bold" className="text-cobalt" />
                    </div>
                    <h1 className="font-heading text-3xl font-black">Notification preferences</h1>
                </div>
                <p className="text-sm text-ink/60 max-w-xl">
                    We cap push notifications at 3 per day — pick what should fill those slots.
                    Interview reminders and offers always break through, no matter your budget.
                </p>
            </header>

            {loading ? (
                <div className="text-xs text-ink/50">Loading…</div>
            ) : !prefs ? (
                <div className="text-xs text-red-600">Couldn't load preferences.</div>
            ) : (
                <div className="bg-white border border-ink/10 divide-y divide-ink/10">
                    {CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        const on = Boolean(prefs[cat.key]);
                        return (
                            <div
                                key={cat.key}
                                className="flex items-start gap-4 p-4 sm:p-5"
                                data-testid={`notif-prefs-row-${cat.key}`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                    on ? "bg-cobalt/10 text-cobalt" : "bg-ink/5 text-ink/40"
                                }`}>
                                    <Icon size={18} weight="bold" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-bold text-sm">{cat.label}</h3>
                                        {cat.priority === "highest" && (
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5">
                                                Always breaks through
                                            </span>
                                        )}
                                        {cat.staffOnly && (
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5">
                                                Staff only
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-ink/60 mt-1">{cat.desc}</p>
                                </div>
                                <Toggle
                                    on={on}
                                    disabled={savingKey === cat.key}
                                    onClick={() => toggle(cat.key)}
                                    testid={`notif-prefs-toggle-${cat.key}`}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Test push helper */}
            <div className="mt-6 bg-cream border border-ink/10 p-4 sm:p-5 flex items-start gap-4">
                <CheckCircle size={20} weight="bold" className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                    <p className="font-bold text-sm mb-1">Test your push</p>
                    <p className="text-xs text-ink/60 mb-3">
                        Fire a sample notification to confirm this device is subscribed.
                        Nothing happens if you haven't granted permission yet.
                    </p>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const r = await api.post("/me/push/test");
                                if (r?.data?.sent) toast.success("Sent — check your notification tray");
                                else toast.info(r?.data?.reason || "No active push subscription on this device");
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Couldn't send test");
                            }
                        }}
                        data-testid="notif-prefs-test-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-ink/20 text-[11px] font-black uppercase tracking-wider hover:border-cobalt hover:text-cobalt"
                    >
                        Send test notification
                    </button>
                </div>
            </div>
        </div>
    );
}

function Toggle({ on, disabled, onClick, testid }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled}
            onClick={onClick}
            data-testid={testid}
            data-state={on ? "on" : "off"}
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cobalt/40 disabled:opacity-50 ${
                on ? "bg-cobalt" : "bg-ink/20"
            }`}
        >
            <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    on ? "translate-x-5" : "translate-x-0"
                }`}
            />
        </button>
    );
}
